import React, { useState, useEffect } from 'react';
import { Menu, X, Smartphone } from 'lucide-react';
import { Button } from './Button';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Insights', path: 'https://app.menshealthclinic.africa/insights', external: true },
    { name: 'Contact', path: '/contact' },
  ];

  const handleGetApp = () => {
    navigate('/waiting-list');
  };

  const isHome = location.pathname === '/';

  return (
    <nav 
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 border-b ${
        isScrolled || !isHome
          ? 'bg-white/90 backdrop-blur-md shadow-sm border-gray-100 py-3' 
          : 'bg-transparent border-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-6 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://09da45aa8861e9e09f066d7bf0bbd9f0.cdn.bubble.io/f1765291597180x898425436812830000/Final-Logo-horizontal.jpg" 
              alt="Men's Health Clinic Africa" 
              className="h-12 w-auto bg-white rounded-xl p-2 object-contain shadow-sm"
              onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement!.innerHTML += '<span class="text-2xl font-bold text-secondary tracking-tight">MHC<span class="text-primary">Africa</span></span>';
              }}
            />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-10">
          {navLinks.map((link) => (
            link.external ? (
              <a
                key={link.name}
                href={link.path}
                className={`text-sm font-semibold transition-colors tracking-wide ${
                  isHome && !isScrolled ? 'text-gray-600 hover:text-primary' : 'text-gray-600 hover:text-primary'
                }`}
              >
                {link.name}
              </a>
            ) : (
              <Link 
                key={link.name} 
                to={link.path} 
                className={`text-sm font-semibold transition-colors tracking-wide ${
                  location.pathname === link.path 
                    ? 'text-primary' 
                    : (isHome && !isScrolled ? 'text-gray-600 hover:text-primary' : 'text-gray-600 hover:text-primary')
                }`}
              >
                {link.name}
              </Link>
            )
          ))}
        </div>

        {/* CTA */}
        <div className="hidden lg:block">
          <Button className="px-5 py-2 text-sm gap-2 shadow-lg shadow-primary/20 hover:shadow-primary/30" onClick={handleGetApp}>
            <Smartphone size={16} />
            Get the App
          </Button>
        </div>

        {/* Mobile Toggle */}
        <button 
          className="lg:hidden text-secondary p-2 -mr-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="absolute top-full left-0 w-full bg-white/95 backdrop-blur-xl border-t border-gray-100 shadow-xl p-6 flex flex-col gap-2 lg:hidden animate-in slide-in-from-top-5">
           {navLinks.map((link) => (
            link.external ? (
              <a
                key={link.name}
                href={link.path}
                className="text-lg font-medium py-3 px-4 rounded-xl transition-colors text-secondary hover:bg-gray-50"
              >
                {link.name}
              </a>
            ) : (
              <Link 
                key={link.name} 
                to={link.path} 
                className={`text-lg font-medium py-3 px-4 rounded-xl transition-colors ${
                    location.pathname === link.path ? 'bg-blue-50 text-primary' : 'text-secondary hover:bg-gray-50'
                }`}
              >
                {link.name}
              </Link>
            )
          ))}
          <div className="pt-4 mt-2 border-t border-gray-100">
            <Button className="w-full justify-center gap-2 py-3" onClick={handleGetApp}>
                <Smartphone size={18} />
                Get the App
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};